import React from "react";
import {
  Row,
  Col,
  Form,
  FormGroup,
  Label,
  Input,
  Card,
  CardBody,
} from "reactstrap";
import Select from "react-select";
import { SettingsProps } from "../../types/settings";
import SettingsButtons from "./SettingsButtons";
import { Formik } from "formik";
import { shippingSettingsValidation } from "../../rules/settings";
import { ShippingSettingsValues } from "../../types/settings";

const ShippingSettings: React.FC<SettingsProps> = ({
  onGlobalCancel,
  isSaving,
  onSave,
}) => {
  const initialValues: ShippingSettingsValues = {
    defaultShippingMethod: null,
    processingTime: 0,
    freeShippingEnabled: false,
    freeShippingThreshold: 0,
    shippingZones: [],
    localShippingRate: 0,
    domesticShippingRate: 0,
    enableLocalPickup: false,
    enableExpressDelivery: false,
    pickupLocations: "",
  };

  const handleSave = async (values: ShippingSettingsValues) => {
    await onSave(async () => {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));
      // TODO: Add actual API call here
    });
  };

  return (
    <div className="bg-transparent">
      <h5 className="mb-4">Shipping & Delivery Settings</h5>
      <Formik
        initialValues={initialValues}
        validationSchema={shippingSettingsValidation}
        onSubmit={handleSave}
        validateOnMount={true}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          setFieldValue,
          handleSubmit,
        }) => (
          <Form>
            <Card className="mb-4">
              <CardBody>
                <h6 className="mb-3">Shipping Methods</h6>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="defaultShippingMethod">
                        Default Shipping Method
                      </Label>
                      <Select
                        id="defaultShippingMethod"
                        name="defaultShippingMethod"
                        value={values.defaultShippingMethod}
                        onChange={(option) =>
                          setFieldValue("defaultShippingMethod", option)
                        }
                        options={[
                          { value: "standard", label: "Standard Delivery" },
                          { value: "express", label: "Express Delivery" },
                          { value: "pickup", label: "Local Pickup" },
                        ]}
                        className={
                          touched.defaultShippingMethod &&
                          errors.defaultShippingMethod
                            ? "is-invalid"
                            : ""
                        }
                      />
                      {touched.defaultShippingMethod &&
                        errors.defaultShippingMethod && (
                          <div className="invalid-feedback d-block">
                            {errors.defaultShippingMethod as string}
                          </div>
                        )}
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="processingTime">Processing Time (Days)</Label>
                      <Input
                        type="number"
                        id="processingTime"
                        name="processingTime"
                        placeholder="Enter processing time"
                        min="0"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.processingTime}
                        invalid={
                          !!(touched.processingTime && errors.processingTime)
                        }
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <hr className="my-4" />

                <h6 className="mb-3">Free Shipping Rules</h6>
                <Row>
                  <Col md={6}>
                    <FormGroup check className="mb-3">
                      <Input
                        type="checkbox"
                        id="freeShippingEnabled"
                        name="freeShippingEnabled"
                        onChange={handleChange}
                        checked={values.freeShippingEnabled}
                      />
                      <Label check for="freeShippingEnabled">
                        Enable Free Shipping
                      </Label>
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="freeShippingThreshold">
                        Free Shipping Threshold
                      </Label>
                      <Input
                        type="number"
                        id="freeShippingThreshold"
                        name="freeShippingThreshold"
                        placeholder="Enter minimum amount for free shipping"
                        min="0"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.freeShippingThreshold}
                        invalid={
                          !!(
                            touched.freeShippingThreshold &&
                            errors.freeShippingThreshold
                          )
                        }
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <hr className="my-4" />

                <h6 className="mb-3">Shipping Zones</h6>
                <Row>
                  <Col md={12}>
                    <FormGroup>
                      <Label for="shippingZones">Manage Shipping Zones</Label>
                      <Select
                        isMulti
                        id="shippingZones"
                        name="shippingZones"
                        value={values.shippingZones}
                        onChange={(options) =>
                          setFieldValue("shippingZones", options)
                        }
                        options={[
                          { value: "local", label: "Local (Within City)" },
                          { value: "domestic", label: "Domestic" },
                          { value: "international", label: "International" },
                        ]}
                        className={
                          touched.shippingZones && errors.shippingZones
                            ? "is-invalid"
                            : ""
                        }
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <Row className="mt-3">
                  <Col md={6}>
                    <FormGroup>
                      <Label for="localShippingRate">Local Shipping Rate</Label>
                      <Input
                        type="number"
                        id="localShippingRate"
                        name="localShippingRate"
                        placeholder="Enter rate for local shipping"
                        min="0"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.localShippingRate}
                        invalid={
                          !!(
                            touched.localShippingRate &&
                            errors.localShippingRate
                          )
                        }
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="domesticShippingRate">
                        Domestic Shipping Rate
                      </Label>
                      <Input
                        type="number"
                        id="domesticShippingRate"
                        name="domesticShippingRate"
                        placeholder="Enter rate for domestic shipping"
                        min="0"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.domesticShippingRate}
                        invalid={
                          !!(
                            touched.domesticShippingRate &&
                            errors.domesticShippingRate
                          )
                        }
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <hr className="my-4" />

                <h6 className="mb-3">Delivery Options</h6>
                <Row>
                  <Col md={6}>
                    <FormGroup check className="mb-3">
                      <Input
                        type="checkbox"
                        id="enableLocalPickup"
                        name="enableLocalPickup"
                        onChange={handleChange}
                        checked={values.enableLocalPickup}
                      />
                      <Label check for="enableLocalPickup">
                        Enable Local Pickup
                      </Label>
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup check className="mb-3">
                      <Input
                        type="checkbox"
                        id="enableExpressDelivery"
                        name="enableExpressDelivery"
                        onChange={handleChange}
                        checked={values.enableExpressDelivery}
                      />
                      <Label check for="enableExpressDelivery">
                        Enable Express Delivery
                      </Label>
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col md={12}>
                    <FormGroup>
                      <Label for="pickupLocations">Pickup Locations</Label>
                      <Input
                        type="textarea"
                        id="pickupLocations"
                        name="pickupLocations"
                        rows={3}
                        placeholder="Enter pickup locations (one per line)"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.pickupLocations}
                        invalid={
                          !!(touched.pickupLocations && errors.pickupLocations)
                        }
                      />
                    </FormGroup>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            <SettingsButtons
              onSave={handleSubmit}
              onCancel={onGlobalCancel}
              saveDisabled={Object.keys(errors).length > 0}
              isSaving={isSaving}
            />
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default ShippingSettings;
